import { useCallback, useContext, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Flex } from 'foundations-components/components/Flex';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Form } from 'foundations-components/transitional/components/Form';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { Text } from 'foundations-components/components/Text';
import { useGetMeetingLinkLazyQuery } from '../../../__generated__/chirp/com/hubspot/product/updates/survey/rpc/MeetingLinkRpc.hooks';
import { logError } from '../../app/utils/handleError';
import { trackMeetScheduleLinkClick } from '../../app/utils/tracking/tracking';
import { useAppSelector } from '../../app/indexReducer';
import { selectAuthenticatedUser } from '../../app/auth/AuthSelectors';
import { selectHasPmOutreachGate } from '../../app/auth/GateSelectors';
import { MeetingScheduleLinkClickTrackerContext } from '../contexts/Tracking';
import useFeedbackRateLimit from '../hooks/useFeedbackRateLimit';
import BetaFeedbackFormSection from './beta-feedback-modal/BetaFeedbackFormSection';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FeedbackModalHeader = () => /*#__PURE__*/_jsx(DialogHeader, {
  blockSize: "5px",
  paddingBlockStart: "0",
  paddingBlockEnd: "0"
});
const FeedbackModalFooter = () => /*#__PURE__*/_jsx(DialogFooter, {
  blockSize: "5px",
  paddingBlock: "0",
  paddingInline: "0"
});
function BetaFeedbackModal({
  rolloutId,
  productUpdateId,
  productUpdateTitle = '',
  lastSubmittedDate,
  onClose
}) {
  const user = useAppSelector(selectAuthenticatedUser);
  const {
    screen,
    subscreen,
    origin
  } = useContext(MeetingScheduleLinkClickTrackerContext);
  const hasPmOutreachGate = useAppSelector(selectHasPmOutreachGate);
  const feedbackFormSectionRef = useRef(null);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackStep, setFeedbackStep] = useState();
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const {
    checkIsAllowedToSubmitFeedback,
    updateRateLimit
  } = useFeedbackRateLimit(user === null || user === void 0 ? void 0 : user.user_id);
  const [hasRateLimitError, setHasRateLimitError] = useState(!checkIsAllowedToSubmitFeedback(productUpdateId));
  const showLastSubmission = feedbackStep === 'rating' && lastSubmittedDate;
  const onSubmit = e => {
    var _feedbackFormSectionR;
    e.preventDefault();
    setIsSubmittingFeedback(true);
    (_feedbackFormSectionR = feedbackFormSectionRef.current) === null || _feedbackFormSectionR === void 0 || _feedbackFormSectionR.onSubmit();
  };
  const [getMeetingScheduleLink, {
    data: meetingScheduleLinkData,
    loading: isLoadingMeetingScheduleLink
  }] = useGetMeetingLinkLazyQuery({
    onError: error => {
      logError({
        componentName: 'BetaFeedbackModal',
        error,
        extra: {
          message: error.message
        }
      });
    }
  });
  const meetingScheduleLink = meetingScheduleLinkData === null || meetingScheduleLinkData === void 0 ? void 0 : meetingScheduleLinkData.meetingLink;
  const onFeedbackSubmitCompleted = feedback => {
    setFeedbackSubmitted(true);
    setIsSubmittingFeedback(false);
    updateRateLimit(productUpdateId);
    if (hasPmOutreachGate && feedback !== null && feedback !== void 0 && feedback.handRaised) {
      getMeetingScheduleLink({
        variables: {
          rolloutId
        }
      });
    }
  };
  const onFeedbackSubmitError = error => {
    if ('errorType' in error && error.errorType === 'TOO_MANY_REQUESTS') {
      setIsSubmittingFeedback(false);
      setHasRateLimitError(true);
      logError({
        componentName: 'BetaFeedbackModal',
        error
      });
      return;
    }
    setIsSubmittingFeedback(false);
    logError({
      componentName: 'BetaFeedbackModal',
      error
    });
    FloatingAlertStore.addAlert({
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.errors.betaFeedbackSubmitFailed.title"
      }),
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app.errors.betaFeedbackSubmitFailed.message"
      }),
      type: 'danger'
    });
  };
  const renderTitle = useCallback(() => {
    var _feedbackFormSectionR2;
    const rating = (_feedbackFormSectionR2 = feedbackFormSectionRef.current) === null || _feedbackFormSectionR2 === void 0 ? void 0 : _feedbackFormSectionR2.getFeedback().rating;
    switch (rating) {
      case 'POSITIVE':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betaFeedback.modal.positiveTitle"
        });
      case 'NEUTRAL':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betaFeedback.modal.neutralTitle"
        });
      case 'NEGATIVE':
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betaFeedback.modal.negativeTitle"
        });
      default:
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app.betaFeedback.modal.title",
          options: {
            productUpdateTitle
          }
        });
    }
  }, [productUpdateTitle]);
  if (hasRateLimitError) {
    return /*#__PURE__*/_jsxs(Modal, {
      use: "conversational",
      children: [/*#__PURE__*/_jsx(FeedbackModalHeader, {}), /*#__PURE__*/_jsx(DialogBody, {
        paddingBlockStart: "1000",
        children: /*#__PURE__*/_jsxs(Flex, {
          justifyContent: "center",
          gap: "1000",
          children: [/*#__PURE__*/_jsx(Illustration, {
            name: "nps-question-thank-you",
            width: 80
          }), /*#__PURE__*/_jsxs(Flex, {
            paddingBlock: "500",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            children: [/*#__PURE__*/_jsx(H4, {
              typography: "heading-200",
              textAlign: "left",
              marginBlockEnd: "200",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.betaFeedback.modal.rateLimited"
              })
            }), /*#__PURE__*/_jsx(Paragraph, {
              textAlign: "left",
              marginBlockEnd: "100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.betaFeedback.modal.rateLimitedDescription"
              })
            }), /*#__PURE__*/_jsx(Button, {
              use: "link",
              paddingBlock: "0",
              size: "xs",
              onClick: onClose,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.betaFeedback.modal.close"
              })
            })]
          })]
        })
      }), /*#__PURE__*/_jsx(FeedbackModalFooter, {})]
    });
  }
  return !feedbackSubmitted ? /*#__PURE__*/_jsxs(Modal, {
    use: "conversational",
    children: [/*#__PURE__*/_jsx(FeedbackModalHeader, {}), /*#__PURE__*/_jsxs(DialogBody, {
      paddingBlockStart: "1000",
      paddingBlockEnd: "600",
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        size: "md",
        color: "icon-core-default",
        onClick: onClose
      }), /*#__PURE__*/_jsxs(Form, {
        onSubmit: onSubmit,
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h5",
          children: /*#__PURE__*/_jsx("span", {
            style: {
              wordBreak: 'break-word'
            },
            children: renderTitle()
          })
        }), /*#__PURE__*/_jsx(BetaFeedbackFormSection, {
          ref: feedbackFormSectionRef,
          rolloutId: rolloutId,
          productUpdateId: productUpdateId,
          onStepChangeCb: nextStep => {
            setFeedbackStep(nextStep);
          },
          onCompletedCb: onFeedbackSubmitCompleted,
          onErrorCb: onFeedbackSubmitError
        }), feedbackStep === 'feedback' && /*#__PURE__*/_jsx(Flex, {
          justifyContent: "center",
          children: /*#__PURE__*/_jsx(LoadingButton, {
            "data-test-id": "feedback-submit-button",
            use: "primary",
            type: "submit",
            loading: isSubmittingFeedback,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betaFeedback.modal.submit"
            })
          })
        })]
      })]
    }), showLastSubmission && /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      blockSize: "40px",
      borderBlockStartWidth: "100",
      borderBlockStartStyle: "solid",
      borderBlockStartColor: "border-core-default",
      children: /*#__PURE__*/_jsx(Text, {
        typography: "detail-100",
        color: "text-core-default",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "app.betaFeedback.modal.lastSubmission_jsx",
          elements: {
            FormattedDateTime
          },
          options: {
            type: 'userTz',
            value: lastSubmittedDate,
            format: 'LL'
          }
        })
      })
    }), /*#__PURE__*/_jsx(FeedbackModalFooter, {})]
  }) : /*#__PURE__*/_jsxs(Modal, {
    use: "conversational",
    children: [/*#__PURE__*/_jsx(FeedbackModalHeader, {}), /*#__PURE__*/_jsx(DialogBody, {
      paddingBlockStart: "1000",
      children: /*#__PURE__*/_jsxs(Flex, {
        justifyContent: "center",
        gap: "1000",
        children: [/*#__PURE__*/_jsx(Illustration, {
          name: "nps-question-thank-you",
          width: 80
        }), /*#__PURE__*/_jsxs(Flex, {
          paddingBlock: "500",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          children: [/*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betaFeedback.modal.thankYou"
            })
          }), /*#__PURE__*/_jsx(Paragraph, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betaFeedback.modal.thankYouDescription"
            })
          }), hasPmOutreachGate && /*#__PURE__*/_jsxs(_Fragment, {
            children: [isLoadingMeetingScheduleLink && /*#__PURE__*/_jsx(LoadingSpinner, {
              use: "link",
              size: "xs"
            }), meetingScheduleLink && /*#__PURE__*/_jsx(Link, {
              href: meetingScheduleLink,
              external: true,
              paddingBlockEnd: "200",
              onClick: () => {
                if (subscreen) {
                  trackMeetScheduleLinkClick({
                    screen,
                    subscreen,
                    origin,
                    productUpdateId: productUpdateId.toString(),
                    action: 'click meeting schedule link'
                  });
                }
              },
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.betaFeedback.modal.chatWithProductTeam"
              })
            })]
          }), /*#__PURE__*/_jsx(Button, {
            use: "link",
            paddingBlock: "0",
            size: "xs",
            onClick: onClose,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "app.betaFeedback.modal.close"
            })
          })]
        })]
      })
    }), /*#__PURE__*/_jsx(FeedbackModalFooter, {})]
  });
}
export default BetaFeedbackModal;